"use client";

import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { getPusherClient } from "@/lib/pusher-client";
import { toast } from "sonner";

export interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  relatedId?: string;
  relatedType?: string;
  isRead: boolean;
  readAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface AgentNotification {
  type: "calendar_response" | "showing_update";
  showingId: string;
  listingId: string;
  listingTitle: string;
  customerName: string;
  customerEmail: string;
  action: "accepted" | "declined" | "tentative";
  timestamp: string;
  message: string;
}

export function useNotifications() {
  const { data: session, status } = useSession();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch notifications from server
  const fetchNotifications = async () => {
    if (status !== "authenticated" || !session?.user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch("/api/notifications");
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    try {
      const response = await fetch(`/api/notifications/${notificationId}/read`, {
        method: "PATCH",
      });

      if (response.ok) {
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId ? { ...n, isRead: true, readAt: new Date() } : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const response = await fetch("/api/notifications/read-all", {
        method: "PATCH",
      });

      if (response.ok) {
        setNotifications((prev) =>
          prev.map((n) => ({ ...n, isRead: true, readAt: new Date() }))
        );
        setUnreadCount(0);
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId: string) => {
    try {
      const response = await fetch(`/api/notifications/${notificationId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
        const wasUnread = notifications.find((n) => n.id === notificationId)?.isRead === false;
        if (wasUnread) {
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  // Subscribe to real-time notifications via Pusher
  useEffect(() => {
    if (status === "authenticated" && session?.user?.id) {
      // Initial fetch
      fetchNotifications();

      // Subscribe to Pusher channel
      const pusher = getPusherClient();
      const channel = pusher.subscribe(`user-${session.user.id}`);

      // Listen for agent notifications
      channel.bind("agent-notification", (data: AgentNotification) => {
        // Add to notifications list
        const notification: Notification = {
          id: `temp-${Date.now()}`, // Temporary ID until we fetch from server
          type: data.type,
          title: `Calendar Response: ${data.action}`,
          message: data.message,
          relatedId: data.showingId,
          relatedType: "SHOWING",
          isRead: false,
          createdAt: new Date(data.timestamp),
          updatedAt: new Date(data.timestamp),
        };

        setNotifications((prev) => [notification, ...prev]);
        setUnreadCount((prev) => prev + 1);

        // Show toast notification
        const actionText =
          data.action === "accepted"
            ? "accepted"
            : data.action === "declined"
              ? "declined"
              : "responded tentatively to";

        toast.info(`${data.customerName} ${actionText} showing`, {
          description: data.listingTitle,
          action: {
            label: "View",
            onClick: () => {
              window.location.href = `/dashboard/showings`;
            },
          },
        });

        // Refresh from server to get persistent ID
        setTimeout(fetchNotifications, 1000);
      });

      return () => {
        channel.unbind_all();
        channel.unsubscribe();
      };
    } else {
      setIsLoading(false);
    }
  }, [session, status]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    fetchNotifications,
  };
}
